import connectAsyncToRoom from '@webclient/state/slices/search-session/thunks/connectAsyncToRoom';
import createAsyncSearchThread from '@webclient/state/slices/search-session/thunks/createAsyncSearchThread';
import disconnectFromRoom from '@webclient/state/slices/search-session/thunks/disconnectAsyncRoom';
import { roomInstance } from '@webclient/state/slices/search-session/utils/room-manager';

import {
  HistoryResultType,
  SearchResultType,
} from '@webclient/shared-types/search-result/streaming';
import { VoiceType } from '@webclient/shared-types/settings';

import {
  createListenerMiddleware,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { LivekitError } from 'livekit-client';

type StateType = {
  result: SearchResultType;
  historyResults: HistoryResultType[];
  isConnected: boolean;
  error: {
    type: 'connectToRoom' | 'createSearchThread' | 'sendMessage';
    message: string;
    code?: number | string;
  } | null;
  threadId: string | null;
  token: string | null;
  isReceivingMessages: boolean;
  searchThreadStatus: 'pending' | 'fulfilled' | 'rejected' | 'idle';
  attachments: Array<{
    key: string | null;
    id: string;
    name: string;
    type: string;
    isPending: boolean;
    isProcessingByAI: boolean;
    error: string | null;
    progress: number;
  }>;
  textToSpeech: {
    currentPlayingBlockId: string | null;
    readyTo: 'play' | 'stop';
    isLoading: boolean;
  };
  voiceChatMode: {
    mode: boolean;
    state: 'loading' | 'thinking' | 'responding' | 'listening' | null;
  };
};

const initialState: StateType = {
  isConnected: false,
  error: null,
  threadId: null,
  token: null,
  isReceivingMessages: false,
  attachments: [],
  searchThreadStatus: 'idle',
  result: {
    botSwitches: [],
    bot: null,
    historyBlockId: null,
    searchQuery: '',
    followups: {
      followups: [],
      type: 'followup',
    },
    loading: {
      isLoading: true,
      type: null,
      composed: [],
      response: '',
      stream_counter: 0,
    },
    layout: {
      answer: {
        composed: [],
        id: '',
        text: '',
        type: 'answer',
        search_query: '',
        user_query: '',
      },
      source: {
        id: '',
        source: [],
        type: 'source',
      },
      photo: {
        id: '',
        photos: [],
        type: 'photos',
      },
    },
  },
  historyResults: [],
  textToSpeech: {
    currentPlayingBlockId: null,
    isLoading: false,
    readyTo: 'play',
  },
  voiceChatMode: {
    mode: false,
    state: null,
  },
};

const searchSession = createSlice({
  name: 'searchSession',
  initialState,
  reducers: {
    setResult: (state, action: PayloadAction<StateType['result']>) => {
      return { ...state, result: action.payload };
    },
    updateResultWithBot: (
      state,
      action: PayloadAction<StateType['result']['bot']>,
    ) => {
      return {
        ...state,
        result: { ...state.result, bot: action.payload },
      };
    },
    updateResultWithBotSwitch: (
      state,
      action: PayloadAction<SearchResultType['botSwitches'][number]>,
    ) => {
      return {
        ...state,
        result: {
          ...state.result,
          botSwitches: [...state.result.botSwitches, action.payload],
        },
      };
    },
    deleteBotSwitchFromResultById: (state, action: PayloadAction<number>) => {
      const indexToDelete = state.result.botSwitches.findIndex(
        (botSwitch) => botSwitch.id === action.payload,
      );

      if (indexToDelete === -1) {
        console.error('Bot switch not found in result');
        return;
      }

      state.result.botSwitches.splice(indexToDelete, 1);
    },
    deleteBotSwitchFromHistoryResultsById: (
      state,
      action: PayloadAction<{
        id: number;
        index: number;
      }>,
    ) => {
      const resultIndex = state.historyResults.findIndex(
        (_historyResult, index) => index === action.payload.index,
      );
      if (resultIndex === -1) {
        console.error('History result not found');
        return;
      }

      state.historyResults[resultIndex].botSwitches = state.historyResults[
        resultIndex
      ].botSwitches.filter((botSwitch) => botSwitch.id !== action.payload.id);
    },
    updateResultWithSearchQuery: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        result: { ...state.result, searchQuery: action.payload },
      };
    },
    clearResult: (state) => {
      return {
        ...state,
        result: initialState.result,
      };
    },
    setHistrotyResults: (
      state,
      action: PayloadAction<StateType['historyResults'][number]>,
    ) => {
      state.historyResults.push(action.payload);
    },
    startReceivingMessages: (state) => {
      return state;
    },
    finishReceivingMessages: (state) => {
      return state;
    },
    setIsReceivingMessages: (state, action: PayloadAction<boolean>) => {
      return { ...state, isReceivingMessages: action.payload };
    },
    addAttachment: (
      state,
      action: PayloadAction<
        Omit<
          StateType['attachments'][number],
          'error' | 'key' | 'isProcessingByAI'
        >
      >,
    ) => {
      const isAttachmentAlreadyAdded = state.attachments.some(
        (attachment) => attachment.id === action.payload.id,
      );

      if (isAttachmentAlreadyAdded) {
        console.error('Attachment already added');
        return state;
      }

      return {
        ...state,
        attachments: [
          ...state.attachments,
          { ...action.payload, key: null, error: null, isProcessingByAI: true },
        ],
      };
    },
    updateAttachmentByName: (
      state,
      action: PayloadAction<StateType['attachments'][number]>,
    ) => {
      const index = state.attachments.findIndex(
        (attachment) => attachment.id === action.payload.id,
      );

      if (index === -1) {
        return state;
      }

      return {
        ...state,
        attachments: [
          ...state.attachments.slice(0, index),
          action.payload,
          ...state.attachments.slice(index + 1),
        ],
      };
    },
    updateAttachmentProgress: (
      state,
      action: PayloadAction<
        Pick<
          StateType['attachments'][number],
          'name' | 'progress' | 'error' | 'id'
        >
      >,
    ) => {
      const attachment = state.attachments.find(
        (a) => a.id === action.payload.id,
      );

      if (!attachment) {
        return state;
      }

      function calculateStatusForUploading(progress: number): boolean {
        return progress > 0 && progress < 50;
      }

      function calculateStatusForProcessing(progress: number): boolean {
        return progress >= 50 && progress < 100;
      }

      attachment.progress = action.payload.progress;
      attachment.error = action.payload.error;
      attachment.isPending = calculateStatusForUploading(
        action.payload.progress,
      );
      attachment.isProcessingByAI = calculateStatusForProcessing(
        action.payload.progress,
      );
    },
    deleteAttachmentById: (state, action: PayloadAction<{ id: string }>) => {
      return {
        ...state,
        attachments: state.attachments.filter(
          (attachment) => attachment.id !== action.payload.id,
        ),
      };
    },
    resetAttachments: (state) => {
      return { ...state, attachments: [] };
    },
    setThreadId: (state, action: PayloadAction<string>) => {
      return { ...state, threadId: action.payload };
    },
    setToken: (state, action: PayloadAction<string>) => ({
      ...state,
      token: action.payload,
    }),
    sendMessageToRoom: (state, _action: PayloadAction<string>) => state,
    updateTextToSpeechStatus: (
      state,
      action: PayloadAction<StateType['textToSpeech']['readyTo']>,
    ) => {
      state.textToSpeech.readyTo = action.payload;
    },
    updateTextToSpeechLoading: (
      state,
      action: PayloadAction<StateType['textToSpeech']['isLoading']>,
    ) => {
      state.textToSpeech.isLoading = action.payload;
    },
    updateTextToSpeechCurrentPlayingBlock: (
      state,
      action: PayloadAction<StateType['textToSpeech']['currentPlayingBlockId']>,
    ) => {
      state.textToSpeech.currentPlayingBlockId = action.payload;
    },
    playTextToSpeech: (
      state,
      _action: PayloadAction<{
        text: string;
        tts: {
          voice: VoiceType;
          speed: number;
        };
        botId: number;
        blockId: string;
      }>,
    ) => {
      return state;
    },
    stopTextToSpeech: (state) => {
      return state;
    },
    interruptTextToSpeech: (state) => {
      return state;
    },
    resetTextToSpeech: (state) => {
      state.textToSpeech.currentPlayingBlockId = null;
      state.textToSpeech.isLoading = false;
      state.textToSpeech.readyTo = 'play';
      return state;
    },
    voiceModChat: (
      state,
      action: PayloadAction<{
        realtime: boolean;
        bot: StateType['result']['bot'];
      }>,
    ) => {
      state.voiceChatMode.mode = action.payload.realtime;
      state.result.bot = action.payload.bot;
    },
    toggleVoiceChatMode: (state, action: PayloadAction<boolean>) => {
      state.voiceChatMode.mode = action.payload;
    },
    updateVoiceModChatStatus: (
      state,
      action: PayloadAction<
        'thinking' | 'responding' | 'listening' | 'loading' | null
      >,
    ) => {
      state.voiceChatMode.state = action.payload;
    },
    startVoiceChatMode: (state) => state,
    stopVoiceChatMode: (state) => state,
    sendAttachmentToRoom: (
      state,
      _action: PayloadAction<{
        mimeType: string;
        key: string;
        name: string;
        id: string;
      }>,
    ) => state,
    deleteAttachmentFromRoomByKey: (
      state,
      _action: PayloadAction<{ key: string }>,
    ) => state,
    resetThread: (state) => {
      roomInstance.reset();
      state.attachments = initialState.attachments;
      state.error = initialState.error;
      state.result = initialState.result;
      state.historyResults = initialState.historyResults;
      state.isReceivingMessages = initialState.isReceivingMessages;
      state.threadId = initialState.threadId;
      state.token = initialState.token;
      state.isConnected = initialState.isConnected;
      state.textToSpeech = initialState.textToSpeech;
      return;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(connectAsyncToRoom.fulfilled, (state) => {
      return {
        ...state,
        isConnected: true,
      };
    });
    builder.addCase(connectAsyncToRoom.rejected, (state, action) => {
      state.isConnected = false;
      state.error = {
        type: 'connectToRoom',
        message: (action.payload as LivekitError).message,
        code: (action.payload as LivekitError).code,
      };
      return state;
    });
    builder.addCase(disconnectFromRoom.fulfilled, (state) => {
      state.isConnected = false;
      state.threadId = null;
      state.token = null;
      state.isReceivingMessages = false;
      return state;
    });
    builder.addCase(createAsyncSearchThread.fulfilled, (state, action) => {
      state.threadId = action.payload.threadId;
      state.token = action.payload.token;
      state.searchThreadStatus = 'fulfilled';
      return state;
    });
    builder.addCase(createAsyncSearchThread.rejected, (state, action) => {
      state.searchThreadStatus = 'rejected';
      state.error = {
        type: 'createSearchThread',
        message: (action.payload as AxiosError).message,
        code: (action.payload as AxiosError).code,
      };
      return state;
    });
    builder.addCase(createAsyncSearchThread.pending, (state) => {
      state.searchThreadStatus = 'pending';
    });
    builder.addMatcher(createAsyncSearchThread.settled, (state) => {
      state.searchThreadStatus = 'idle';
      return state;
    });
  },
});

export const voiceChatModeListener = createListenerMiddleware();

export const {
  setResult,
  updateResultWithSearchQuery,
  updateResultWithBot,
  updateResultWithBotSwitch,
  deleteBotSwitchFromHistoryResultsById,
  deleteBotSwitchFromResultById,
  clearResult,
  setHistrotyResults,
  sendMessageToRoom,
  setThreadId,
  setToken,
  addAttachment,
  updateAttachmentProgress,
  updateAttachmentByName,
  deleteAttachmentById,
  resetAttachments,
  resetThread,
  finishReceivingMessages,
  startReceivingMessages,
  setIsReceivingMessages,
  playTextToSpeech,
  stopTextToSpeech,
  interruptTextToSpeech,
  updateTextToSpeechCurrentPlayingBlock,
  updateTextToSpeechLoading,
  updateTextToSpeechStatus,
  resetTextToSpeech,
  sendAttachmentToRoom,
  deleteAttachmentFromRoomByKey,
  voiceModChat,
  toggleVoiceChatMode,
  updateVoiceModChatStatus,
  startVoiceChatMode,
  stopVoiceChatMode,
} = searchSession.actions;

export default searchSession.reducer;
